import { useCallback, useState } from 'react'
import { useToast } from '@/hooks/use-toast'
import AuthCard from './auth-card'
import PasswordRequirements from './password-requirements'
import Label from '../label'
import ToggleButton from '../toggle-button'
import Button from '../button'
import AuthFromWrapper from './form-wraper'
import AuthHeader from './header'
import AuthContainer from './auth-container'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Loader from '../loader'
import { AUTH_CONFIG } from '../../auth/config'

const SetNewPassword = () => {
  const { toast } = useToast()
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showRetypePassword, setShowRetypePassword] = useState(false)
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword)
  }
  const toggleRetypePasswordVisibility = () => {
    setShowRetypePassword(!showRetypePassword)
  }

  const handlePasswordChange = useCallback(
    (e) => {
      setPassword(e.target.value)
    },
    [setPassword],
  )

  const handleRetypePasswordChange = useCallback(
    (e) => {
      if (password !== e.target.value) {
        setError('Passwords do not match!')
      } else {
        setError('')
      }
    },
    [password],
  )

  const handleResetPassword = useCallback(
    async (e) => {
      e.preventDefault()
      setLoading(true)

      const token = searchParams.get('token')
      if (!token) {
        toast({
          title: 'Error!',
          description: 'Missing reset token. Please use the link from your email.',
          variant: 'destructive',
        })
        setLoading(false)
        return
      }

      try {
        const response = await fetch(`${AUTH_CONFIG.authServerUrl}/api/auth/reset-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, password }),
        })

        if (!response.ok) {
          const data = await response.json().catch(() => ({}))
          throw new Error(data.message || `Password reset failed (${response.status})`)
        }

        toast({
          title: 'Password reset successful!',
          description: 'You can now log in with your new password.',
          variant: 'success',
        })
        // Keep loading state active during redirect delay
        setTimeout(() => {
          navigate('/auth/login')
        }, 3000)
        return
      } catch (err) {
        toast({
          title: 'Error!',
          description: `Something went wrong. ${err.message}`,
          variant: 'destructive',
        })
        setLoading(false)
      }
    },
    [navigate, password, searchParams, toast],
  )

  return (
    <AuthContainer>
      <AuthHeader />
      <AuthFromWrapper>
        <AuthCard title="Set new password">
          <div className="relative max-w-md mx-auto p-4">
            {loading && <Loader />}
            <form onSubmit={handleResetPassword}>
              <PasswordRequirements />
              <div className="mb-4 relative">
                <Label text="New Password" htmlFor="new-password" />
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  id="new-password"
                  name="new-password"
                  className="w-full px-3 py-2 border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="New Password"
                  required
                  onChange={handlePasswordChange}
                />
                <ToggleButton
                  toggleFunc={toggleNewPasswordVisibility}
                  toggled={showNewPassword}
                  showImagePath="/eye.svg"
                  hideImagePath="/eye-closed.svg"
                />
              </div>

              <div className="mb-8 relative">
                <Label text="Retype Password" htmlFor="retype-password" />
                <input
                  type={showRetypePassword ? 'text' : 'password'}
                  id="retype-password"
                  name="retype-password"
                  className="w-full px-3 py-2 border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Retype Password"
                  required
                  onChange={handleRetypePasswordChange}
                />
                <ToggleButton
                  toggleFunc={toggleRetypePasswordVisibility}
                  toggled={showRetypePassword}
                  showImagePath="/eye.svg"
                  hideImagePath="/eye-closed.svg"
                />
              </div>

              {error && <p className="text-xs text-red-500 mb-4">{error}</p>}

              <div className="text-center">
                <Button type="submit" disabled={loading || error ? true : false}>
                  Reset password
                </Button>
              </div>
            </form>
          </div>
        </AuthCard>
      </AuthFromWrapper>
    </AuthContainer>
  )
}

export default SetNewPassword
